import React from 'react';
import {
  Container,
  Stack,
  Text,
  Group,
  Badge,
  LoadingOverlay,
  Card,
  Divider,
  Title,
  Flex,
  Button,
  SimpleGrid
} from '@mantine/core';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { IconEdit, IconArrowLeft, IconTrash } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { useMutation } from '@tanstack/react-query';
import BreadcrumbsComponent from 'components/BreadcrumbsComponent';
import PageTitle from 'components/PageTitle';
import SecondaryButton from 'components/SecondaryButton';
import PrimaryButton from 'components/PrimaryButton';
import ModalPopupConfirmationComponent from 'components/ModalPopupConfirmationComponent';
import { masterUserAdminService } from 'services/dacum/masterUserAdminService';
import { MasterUserAdmin } from 'services/api';
import { showErrorNotification, showSuccessNotification } from 'utils/notification';
import Dates from 'components/Dates';

const MasterUserAdminDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [deleteOpened, { open: openDelete, close: closeDelete }] = useDisclosure();

  const items = [
    { title: 'Master Admin User', href: '/app/dacum/master-user-admin' },
    { title: 'Admin User Details', href: '' }
  ];

  // Fetch user admin details
  const {
    data: userAdminData,
    isLoading,
    error
  } = useQuery({
    queryKey: ['masterUserAdmin', id],
    queryFn: () => masterUserAdminService.getDetail(id!),
    enabled: !!id,
    select: (response) => response.data || (response as MasterUserAdmin)
  });

  const deleteMutation = useMutation({
    mutationFn: (userAdminID: string) => masterUserAdminService.delete(userAdminID),
    onSuccess: () => {
      navigate('/app/dacum/master-user-admin');
      showSuccessNotification({
        title: 'Deleted Successfully!',
        message: 'Admin user has been deleted successfully'
      });
    },
    onError: (error: any) => {
      showErrorNotification({
        title: 'Failed to delete admin user',
        message: error?.message || 'Something went wrong please try again'
      });
    }
  });

  const handleEdit = () => {
    navigate(`/app/dacum/master-user-admin/edit/${id}`);
  };

  const handleDelete = () => {
    openDelete();
  };

  const processDelete = () => {
    if (id) {
      deleteMutation.mutate(id);
    }
    closeDelete();
  };

  const handleBackToList = () => {
    navigate('/app/dacum/master-user-admin');
  };

  const renderAreas = () => {
    if (!userAdminData) return null;

    if (
      userAdminData.listArea &&
      Array.isArray(userAdminData.listArea) &&
      userAdminData.listArea.length > 0
    ) {
      return (
        <Stack gap='xs'>
          <Text size='sm' fw={500}>
            Areas ({userAdminData.listArea.length}):
          </Text>
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing='xs'>
            {userAdminData.listArea.map((area: any, index: any) => (
              <Badge
                key={index}
                variant='light'
                color='green'
                size='sm'
                style={{ justifyContent: 'flex-start' }}
              >
                {area.areaName || area.areaId}
              </Badge>
            ))}
          </SimpleGrid>
        </Stack>
      );
    } else if (userAdminData.areaName || userAdminData.areaID) {
      return (
        <Group>
          <Text size='sm' fw={500} w={150}>
            Area:
          </Text>
          <Badge variant='light' color='green' size='sm'>
            {userAdminData.areaName || userAdminData.areaID}
          </Badge>
        </Group>
      );
    }
    // No areas found
    else {
      return (
        <Group>
          <Text size='sm' fw={500} w={150}>
            Areas:
          </Text>
          <Text size='sm' c='dimmed' fs='italic'>
            No areas assigned
          </Text>
        </Group>
      );
    }
  };

  if (error) {
    return (
      <Container fluid>
        <PageTitle title='Admin User Details' />
        <BreadcrumbsComponent items={items} />

        <Card shadow='xs' radius='md' mt={20} p='xl'>
          <Stack align='center' gap='md'>
            <Text size='lg' fw={500} c='red'>
              Error Loading Admin User Details
            </Text>
            <Text size='sm' c='dimmed'>
              The admin user you're looking for could not be found or there was an error loading the
              details.
            </Text>
            <Button
              leftSection={<IconArrowLeft size={16} />}
              onClick={handleBackToList}
              variant='light'
            >
              Back to Admin Users List
            </Button>
          </Stack>
        </Card>
      </Container>
    );
  }

  if (!id) {
    navigate('/app/dacum/master-user-admin');
    return null;
  }

  return (
    <Container fluid>
      <ModalPopupConfirmationComponent
        buttonText='Delete'
        statusProcess='delete'
        opened={deleteOpened}
        open={openDelete}
        close={closeDelete}
        onSubmit={processDelete}
        itemLabel='admin user'
      />

      <PageTitle title='Admin User Details' />
      <BreadcrumbsComponent items={items} />

      <Card shadow='xs' radius='md' mt={20} pos='relative'>
        <LoadingOverlay visible={isLoading || deleteMutation.isPending} />

        {userAdminData && (
          <Stack gap='md' p='lg'>
            <Flex justify='space-between' align='flex-start' wrap='wrap' gap='md'>
              <div>
                <Title order={2} mb={4}>
                  {userAdminData.adminName}
                </Title>
                <Badge size='lg' variant='light' color='blue' radius='sm'>
                  {userAdminData.adminID}
                </Badge>
              </div>

              <Group gap='sm'>
                <SecondaryButton
                  text='Back to List'
                  onClick={handleBackToList}
                  leftSection={<IconArrowLeft size={16} />}
                />
                <SecondaryButton
                  text='Edit'
                  onClick={handleEdit}
                  leftSection={<IconEdit size={16} />}
                />
                <PrimaryButton
                  text='Delete'
                  onClick={handleDelete}
                  isDelete
                  leftSection={<IconTrash size={16} />}
                />
              </Group>
            </Flex>

            <Divider />

            {/* Admin User Information */}
            <Stack gap='lg'>
              <div>
                <Text size='sm' fw={600} c='dimmed' mb={4}>
                  BASIC INFORMATION
                </Text>

                <Stack gap='md'>
                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      User Admin ID:
                    </Text>
                    <Text size='sm' c='dimmed' style={{ fontFamily: 'monospace' }}>
                      {userAdminData.msUserAdminID}
                    </Text>
                  </Group>

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Binusian ID:
                    </Text>
                    <Badge variant='outline' size='sm'>
                      {userAdminData.adminID}
                    </Badge>
                  </Group>

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Admin Name:
                    </Text>
                    <Text size='sm'>{userAdminData.adminName}</Text>
                  </Group>

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Email:
                    </Text>
                    <Text size='sm'>{userAdminData.adminEmail}</Text>
                  </Group>

                  {userAdminData.beelajarEmail && (
                    <Group>
                      <Text size='sm' fw={500} w={150}>
                        Beelajar Email:
                      </Text>
                      <Text size='sm'>{userAdminData.beelajarEmail}</Text>
                    </Group>
                  )}

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Department:
                    </Text>
                    <Text size='sm'>{userAdminData.department || '-'}</Text>
                  </Group>

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Role:
                    </Text>
                    <Text size='sm'>{userAdminData.role || '-'}</Text>
                  </Group>

                  {/* Updated areas section */}
                  {renderAreas()}
                </Stack>
              </div>

              <Divider />

              <div>
                <Text size='sm' fw={600} c='dimmed' mb={4}>
                  AUDIT INFORMATION
                </Text>

                <Stack gap='md'>
                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Created By:
                    </Text>
                    <Badge variant='light' color='green' size='sm'>
                      {userAdminData.userIn}
                    </Badge>
                  </Group>

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Created Date:
                    </Text>
                    <Dates date={userAdminData.dateIn} dateFormat='dateTime' fz={14} />
                  </Group>

                  {userAdminData.userUp && (
                    <Group>
                      <Text size='sm' fw={500} w={150}>
                        Updated By:
                      </Text>
                      <Badge variant='light' color='orange' size='sm'>
                        {userAdminData.userUp}
                      </Badge>
                    </Group>
                  )}

                  <Group>
                    <Text size='sm' fw={500} w={150}>
                      Updated Date:
                    </Text>
                    <Dates date={userAdminData.dateUp} dateFormat='dateTime' fz={14} />
                  </Group>
                </Stack>
              </div>
            </Stack>
          </Stack>
        )}
      </Card>
    </Container>
  );
};

export default MasterUserAdminDetailPage;
